import { stopSubmit } from 'redux-form'
import { userConstants } from '../constants'
import { userService } from '../../services'
import { request, success, failure, reduxFormFailure } from './helper'
import { isIncapsulaRequest } from '../../utilities/isIncapsulaRequest'
import { getCaptchaToken } from '../../utilities/googleCaptcha.util'

const loadUser = token => dispatch =>
  userService.getUserByToken(token).then(user => {
    if (user) {
      dispatch(success(userConstants.SESSION_USER_SUCCESS, { user }))
    } else {
      dispatch(failure(userConstants.SESSION_USER_FAILURE))
    }
  })

export const getSessionUser = () => dispatch => {
  userService
    .getSessionToken()
    .then(token => dispatch(loadUser(token)))
    .catch(() => dispatch(failure(userConstants.SESSION_USER_FAILURE)))
}

function login(username, password) {
  return async dispatch => {
    dispatch(request(userConstants.LOGIN_REQUEST))
    try {
      const captchaToken = await getCaptchaToken('login')
      const user = await userService.login(username, password, captchaToken)
      const isIncapsulaRequestUser = isIncapsulaRequest(user)
      if (isIncapsulaRequestUser) {
        dispatch(success(userConstants.INCAPSULA_CAPTCHA, { source: isIncapsulaRequestUser }))
      }
      if (user.auth) {
        dispatch(success(userConstants.LOGIN_SUCCESS, { token: user.token }))
        dispatch(loadUser(user.token))
      } else {
        dispatch(failure(userConstants.LOGIN_FAILURE))
      }
    } catch (error) {
      return reduxFormFailure(dispatch, userConstants.LOGIN_FAILURE, error)
    }
  }
}

function logout() {
  return dispatch => {
    userService.logout()
    dispatch(request(userConstants.LOGOUT))
  }
}

function register(user) {
  return async dispatch => {
    dispatch(request(userConstants.REGISTER_REQUEST, user))
    try {
      const captchaToken = await getCaptchaToken('register')
      const registeredUser = await userService.register(user, captchaToken)
      dispatch(success(userConstants.REGISTER_SUCCESS, { token: registeredUser.token }))
      userService.saveUserSession(registeredUser)
    } catch (error) {
      if (error.errors.errorType === 'NotAcceptableError') {
        reduxFormFailure(dispatch, userConstants.REGISTER_FAILURE, error)
      } else if (
        error.errors.errorType === 'BadRequestError' ||
        error.errors.errorType === 'InvalidIPError'
      ) {
        dispatch(failure(userConstants.SECURITY_BLOCKED, error))
      } else if (error.errors[0].errorType === 'ValidationError') {
        reduxFormFailure(dispatch, userConstants.REGISTER_FAILURE, error)
      }
    }
    return dispatch(getSessionUser())
  }
}

function update(user) {
  return async dispatch => {
    dispatch(request(userConstants.UPDATE_REQUEST, user))
    try {
      const captchaToken = await getCaptchaToken('update_user')
      const response = await userService.update(user, captchaToken)
      dispatch(stopSubmit('myAccount', {}))
      try {
        dispatch(success(userConstants.UPDATE_USER_SUCCESS, { user }))
        dispatch(success(userConstants.UPDATE_SUCCESS, response))
      } catch (dispatchError) {
        dispatch(stopSubmit('myAccount', {}))
      }
      return Promise.resolve()
    } catch (error) {
      return reduxFormFailure(dispatch, userConstants.UPDATE_FAILURE, error)
    }
  }
}

const deactivate = user => dispatch =>
  userService
    .deactivate(user)
    .catch(error => reduxFormFailure(dispatch, userConstants.DEACTIVATE_FAILURE, error))

function passwordResetRequest(email) {
  return async dispatch => {
    dispatch(request(userConstants.FORGOT_PASSWORD_REQUEST, email))
    try {
      const response = await userService.passwordResetRequest(email)
      return dispatch(success(userConstants.FORGOT_PASSWORD_SUCCESS, response.token))
    } catch (error) {
      return reduxFormFailure(dispatch, userConstants.FORGOT_PASSWORD_FAILURE, error)
    }
  }
}

function resetPassword(body) {
  return dispatch =>
    userService
      .resetPassword(body)
      .then(response => dispatch(success(userConstants.SET_PASSWORD_SUCCESS, response.token)))
      .catch(error => reduxFormFailure(dispatch, userConstants.SET_PASSWORD_FAILURE, error))
}

const verifyEmail = code => dispatch =>
  userService
    .verifyEmail({ code })
    .then(() => dispatch(getSessionUser()))
    .catch(error => reduxFormFailure(dispatch, userConstants.VERIFY_EMAIL_FAILURE, error))

function verifyPhoneNumber(body) {
  return dispatch =>
    userService
      .verifyPhoneNumber(body)
      .then(() => dispatch(getSessionUser()))
      .catch(error => reduxFormFailure(dispatch, userConstants.VERIFY_PHONE_FAILURE, error))
}

const changePhoneNumber = body => dispatch =>
  userService
    .changePhoneNumber(body)
    .then(() => dispatch(getSessionUser()))
    .catch(error => reduxFormFailure(dispatch, userConstants.VERIFY_PHONE_FAILURE, error))

// prefixed function name with underscore because delete is a reserved word in javascript
function _delete(id) {
  return dispatch => {
    dispatch(request(userConstants.DELETE_REQUEST, id))
    userService
      .delete(id)
      .then(() => dispatch(success(userConstants.DELETE_SUCCESS, id)))
      .catch(error => dispatch(failure(userConstants.DELETE_FAILURE, error)))
  }
}

const changePassword = body => async dispatch => {
  dispatch(request(userConstants.CHANGE_PASSWORD_REQUEST, body))
  try {
    const captchaToken = await getCaptchaToken('change_password')
    await userService.changePassword(body, captchaToken)
    await dispatch(getSessionUser())
    return dispatch(success(userConstants.CHANGE_PASSWORD_SUCCESS))
  } catch (error) {
    reduxFormFailure(dispatch, userConstants.CHANGE_PASSWORD_FAILURE, error)
    throw error
  }
}

const changeEmail = body => dispatch =>
  userService
    .changeEmail(body)
    .then(() => dispatch(getSessionUser()))
    .catch(error => reduxFormFailure(dispatch, null, error))

function clearState() {
  return dispatch => {
    dispatch(request(userConstants.CLEAR_STATE))
  }
}

export default {
  getSessionUser,
  login,
  logout,
  passwordResetRequest,
  resetPassword,
  register,
  update,
  deactivate,
  verifyEmail,
  verifyPhoneNumber,
  delete: _delete,
  changePassword,
  changeEmail,
  clearState,
  changePhoneNumber,
}
